import type { Canceler } from 'axios';
import axios, { isCancel } from 'axios';
import debounce from 'lodash/debounce';
import * as React from 'react';
import { axiosWrapper } from '~/Partials/FetchData/axiosWrapper';

interface RequestHelperProps<ResponseType, RequestDataType> {
    url: string;
    debounceTime?: number;
    autoRun?: boolean;
    method?: 'get' | 'post';
    timeout?: number;
    onLoadData?: (data: ResponseType) => void | undefined;
    onLoadDataError?: (error: string) => void;
    onToggleLoading?: (loadStatus: boolean) => void;
    data?: RequestDataType;
}

export class RequestHelper<ResponseType, RequestDataType = object> extends React.PureComponent<
    RequestHelperProps<ResponseType, RequestDataType>
> {
    static defaultProps: Partial<RequestHelperProps<any, any>> = {
        debounceTime: 0,
        autoRun: true,
        method: 'get',
        timeout: 0,
    };

    cancelToken: Canceler | undefined = undefined;

    makeNetworkRequestDebounce: ReturnType<typeof debounce> | undefined = undefined;

    makeNetworkRequest = () => {
        const { debounceTime } = this.props;

        this.makeNetworkRequestDebounce && this.makeNetworkRequestDebounce.cancel();
        this.makeNetworkRequestDebounce = debounce(() => {
            const { url, method = 'get', timeout, onLoadData, onLoadDataError, data } = this.props;
            axiosWrapper
                .request<ResponseType>({
                    url,
                    method,
                    data,
                    timeout,
                    cancelToken: new axios.CancelToken(token => {
                        this.cancelToken = token;
                    }),
                })
                .then(res => {
                    this.toggleLoading(false);
                    this.cancelToken = undefined;
                    onLoadData && onLoadData(res.data);
                })
                .catch(e => {
                    if (isCancel(e)) {
                        return;
                    }

                    onLoadDataError && onLoadDataError(e.message);

                    console.error(e);
                });
        }, debounceTime);

        this.makeNetworkRequestDebounce();
    };

    toggleLoading = (loadStatus: boolean) => {
        const { onToggleLoading } = this.props;
        onToggleLoading && onToggleLoading(loadStatus);
    };

    componentDidMount = () => {
        const { autoRun } = this.props;

        if (autoRun) {
            this.fetchData();
        }
    };

    componentDidUpdate = (oldProps: RequestHelperProps<ResponseType, RequestDataType>) => {
        const { url } = this.props;

        if (url && oldProps.url !== url) {
            this.fetchData();
        }
    };

    fetchData = () => {
        if (this.cancelToken) {
            this.cancelToken();
        }

        this.toggleLoading(true);
        this.makeNetworkRequest();
    };

    render() {
        return null;
    }
}
